import { useQuery, type QueryClient } from '@tanstack/react-query';

import type { User } from '@/utils/types/User';

import { getUser } from './getUser';
import { useModernGetMeRollout } from './use-modern-get-me-rollout';

export const USER_QUERY_KEY = 'user';

export const userQueryKey = (useModernMe: boolean) => [USER_QUERY_KEY, useModernMe] as const;

type UseUserQueryOptions = {
  enabled?: boolean;
};

/**
 * Shared React Query hook for the current user session.
 * Waits for the modern /me Split flag before fetching so the correct backend is used.
 */
export const useUserQuery = ({ enabled = true }: UseUserQueryOptions = {}) => {
  const { isResolved, isEnabled: useModernMe } = useModernGetMeRollout();

  return useQuery({
    queryKey: userQueryKey(useModernMe),
    queryFn: () => getUser(useModernMe),
    staleTime: Infinity,
    enabled: enabled && isResolved,
  });
};

/** Updates cached user data across legacy and modern /me query keys. */
export const setUserQueryData = (
  queryClient: QueryClient,
  updater: (oldData: User | undefined) => User | undefined,
) => {
  queryClient.setQueriesData<User>({ queryKey: [USER_QUERY_KEY] }, updater);
};
